import { AxiosPromise } from 'axios';
import { useState, useEffect } from 'react';
import { appContext } from '../AppContext';
import { GoFormzServices } from '../AppSettings';
import { apimCall } from '../helpers/api';
import { deepClone, mergeDeep } from '../helpers/utility';
import { IHMetadata } from './IHMetadata';
import {
  Action,
  AppMetadata,
  AvailableActions,
  EventMetadata,
  StepMetadata,
  StepsMetadata,
  StepOutputMetadata,
  Trigger,
  WebhookMetadata,
  AvailableVariables,
  WorkflowStepConfig,
  StepInputMetadata,
  Workflow,
  WorkflowMetadata,
  CommandMetadata,
  WorkflowStep,
  PublishedApp,
  DynamicMetadata,
  AppItemPermissionLevel
} from './workflow-types';

export function getAvailableActionsFromMetadata(publishedApps: PublishedApp[]): AvailableActions {
  const actions: Action[] = [];
  const triggers: Trigger[] = getIHTriggerInfo();

  if (publishedApps) {
    for (const publishedApp of publishedApps) {
      const appMetadata = publishedApp.package.metadata;
      const packageCommands = publishedApp.package.commands;
      const webhooks = appMetadata ? appMetadata.webhooks : null;
      const events = appMetadata ? appMetadata.events : null;
      const globalInstall = appMetadata ? appMetadata.globalInstall : false;
      const permissions = publishedApp.permissions;

      if (packageCommands) {
        for (const command of packageCommands) {
          if (!command.externalNotification) {
            const commandMetadata = appMetadata ? appMetadata.commands.find((x) => x.name === command.key) : null;
            actions.push({
              id: `${publishedApp.appId}${command.key}`,
              name: command.name || command.key,
              description: commandMetadata.description,
              adapterName: 'apptask',
              appId: publishedApp.appId,
              appName: publishedApp.appName,
              appCommand: command.key,
              icon: command.icon || publishedApp.icon,
              globalInstall,
              config: [
                { key: 'appId', defaultValue: publishedApp.appId },
                { key: 'appInstanceId', required: !globalInstall },
                { key: 'appCommand', defaultValue: command.key }
              ],
              permissionLevel: permissions?.find((x) => x.name === command.key)?.level || AppItemPermissionLevel.Unlisted,
              beta: permissions?.find((x) => x.name === command.key)?.beta || false
            });
          }
        }
      }

      if (webhooks) {
        for (const webhook of webhooks) {
          if (webhook.workflowTrigger) {
            triggers.push({
              id: publishedApp.appId,
              name: webhook.name,
              description: webhook.description,
              appId: publishedApp.appId,
              appName: publishedApp.appName,
              icon: publishedApp.icon,
              type: 'appwebhook',
              webhookId: webhook.id,
              config: [
                { key: 'appId', defaultValue: publishedApp.appId },
                { key: 'appInstanceId', required: !globalInstall },
                { key: 'type', defaultValue: 'appwebhook' },
                { key: 'entityId', defaultValue: webhook.id }
              ],
              permissionLevel: permissions?.find((x) => x.name === webhook.id)?.level || AppItemPermissionLevel.Unlisted
            });
          }
        }
      }
      if (events) {
        for (const event of events) {
          triggers.push({
            id: publishedApp.appId,
            name: `${publishedApp.appName} Event - ${event.name}`,
            description: event.description,
            appId: publishedApp.appId,
            appName: publishedApp.appName,
            icon: publishedApp.icon,
            type: 'appevent',
            eventType: event.type,
            config: [
              { key: 'appId', defaultValue: publishedApp.appId },
              { key: 'appInstanceId', required: !globalInstall },
              { key: 'type', defaultValue: 'appevent' },
              { key: 'eventType', defaultValue: event.type, required: true }
            ],
            permissionLevel: permissions?.find((x) => x.name === event.type)?.level || AppItemPermissionLevel.Unlisted
          });
        }
      }
    }
  }

  for (const ihTask of IHMetadata.steps) {
    const taskInfo = getIHTaskInfo(ihTask.adapterName);
    actions.push({
      id: ihTask.adapterName,
      name: taskInfo.name,
      description: null,
      adapterName: ihTask.adapterName,
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: taskInfo.icon,
      globalInstall: true,
      config: ihTask.inputs
        ? ihTask.inputs.map((x) => ({
            key: x.key
          }))
        : []
    });
  }

  return { actions, triggers };
}

export function getIHTriggerInfo(): Trigger[] {
  return [
    {
      id: 'FormCompleted',
      name: 'Form Completed',
      description: 'Kick-off your Workflow when specific users or Group members complete a Form, or when anyone completes a Form using specific Templates.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'FormCompleted',
      type: 'GoFormzEvent',
      config: [
        { key: 'type', defaultValue: 'GoFormzEvent' },
        { key: 'eventType', defaultValue: 'FormComplete' },
        { key: 'entityId' },
        { key: 'userId' },
        { key: 'groupId' },
        { key: 'onlyRunOnInitialCompletion' }
      ]
    },
    {
      id: 'FormTransfer',
      name: 'Receives a Transferred Form',
      description:
        'Kick-off your Workflow when specified users or Groups receive or take ownership of a transferred Form - or, when anyone receives or takes ownership of a transferred Form using specific Templates.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'FormTransfer',
      type: 'GoFormzEvent',
      config: [
        { key: 'type', defaultValue: 'GoFormzEvent' },
        { key: 'eventType', defaultValue: 'FormTransfer' },
        { key: 'entityId' },
        { key: 'userId' },
        { key: 'groupId' }
      ]
    },
    {
      id: 'Schedule',
      name: 'Schedule',
      description: 'Schedule a Workflow to trigger daily, weekly, or monthly.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'Schedule',
      type: 'ScheduledEvent',
      config: [
        { key: 'type', defaultValue: 'ScheduledEvent' },
        { key: 'cronString', required: true },
        { key: 'timeZone', required: true }
      ],
      permissionLevel: AppItemPermissionLevel.Advanced
    },
    {
      id: 'FormPublicForm',
      name: 'Public Form Submitted',
      description:
        'Kick-off your Workflow when specific users or Group members own a submitted Public Form, or when a Public Form is submitted using specific Templates.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'FormPublicForm',
      type: 'GoFormzEvent',
      config: [{ key: 'type', defaultValue: 'GoFormzEvent' }, { key: 'eventType', defaultValue: 'FormPublicShare' }, { key: 'entityId' }],
      permissionLevel: AppItemPermissionLevel.Team
    },
    {
      id: 'ManualTrigger',
      name: 'Manual Trigger',
      description:
        'Trigger a workflow on demand while filling out the form. The form does not have to be completed.  Must be used with the Workflow Trigger Custom Action.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'ManualTrigger',
      type: 'Manual',
      config: [{ key: 'type', defaultValue: 'Manual' }],
      permissionLevel: AppItemPermissionLevel.Unlisted
    },
    {
      id: 'FormDeleted',
      name: 'Form Deleted',
      description: 'Kick-off your Workflow when anyone deletes a Form using specific Templates.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'FormDeleted',
      type: 'GoFormzEvent',
      config: [{ key: 'type', defaultValue: 'GoFormzEvent' }, { key: 'eventType', defaultValue: 'FormDelete' }, { key: 'entityId' }]
    },
    {
      id: 'GoFormzWebhook',
      name: 'GoFormz Webhook',
      description: 'Triggers when a notification from a predefined GoFormz URL.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'Webhook',
      type: 'Webhook.GoFormz',
      config: [{ key: 'type', defaultValue: 'Webhook.GoFormz' }]
    },
    {
      id: 'SalesforceWebhook',
      name: 'Salesforce Webhook',
      description: 'Triggers when a notification from an authenticated Salesforce organization comes in on a predefined GoFormz URL.',
      appId: 'Salesforce',
      appName: 'Salesforce',
      icon: 'Salesforce',
      type: 'Webhook.Salesforce',
      config: [{ key: 'type', defaultValue: 'Webhook.Salesforce' }]
    },
    {
      id: 'FormCreated',
      name: 'Form Created',
      description: 'Kick-off your Workflow when specific users or Group members create a Form, or when anyone creates a Form using specific Templates.',
      appId: 'GoFormz',
      appName: 'GoFormz',
      icon: 'FormCreated',
      type: 'GoFormzEvent',
      config: [
        { key: 'type', defaultValue: 'GoFormzEvent' },
        { key: 'eventType', defaultValue: 'FormCreate' },
        { key: 'entityId' },
        { key: 'userId' },
        { key: 'groupId' }
      ],
      permissionLevel: AppItemPermissionLevel.Unlisted
    }
  ];
}

function getIHTaskInfo(adapterName: string) {
  switch (adapterName) {
    case 'SendEmailTask':
      return { name: 'Send Email', icon: 'Email' };

    case 'CreatePublicFormTask':
      return { name: 'Create Public Form', icon: 'CreateForm' };

    case 'CreateFormTask':
      return { name: 'Create Form', icon: 'CreateForm' };

    case 'UpdateFormTask':
      return { name: 'Update Form', icon: 'GeneralTask' };

    case 'DeleteFormTask':
      return { name: 'Delete Form', icon: 'GeneralTask' };

    case 'ChangeFormStatusTask':
      return { name: 'Change Form Status', icon: 'GeneralTask' };

    case 'ExportFormToPdfTask':
      return { name: 'Export Form To PDF', icon: 'PDF' };

    case 'ExportReportViewToCsvTask':
      return { name: 'Export Report View To CSV', icon: 'CSV' };

    case 'GetFormImagesTask':
      return { name: 'Get Form Images', icon: 'Image' };

    case 'GetFormTask':
      return { name: 'Get Form', icon: 'GetForm' };

    case 'GetFormJsonTask':
      return { name: 'Get Form Json', icon: 'GeneralTask' };

    case 'GetFormTableTask':
      return { name: 'Get Form Table', icon: 'GetForm' };

    case 'FillFormTableTask':
      return { name: 'Fill Form Table', icon: 'GeneralTask' };

    case 'FillFormFieldsTask':
      return { name: 'Fill Form Fields', icon: 'GeneralTask' };

    case 'UpsertDataSourceRowTask':
      return { name: 'Upsert Datasource Row', icon: 'UpsertDatasourceRow' };

    case 'GetDataSourceTask':
      return { name: 'Get Data Source', icon: 'GeneralTask' };

    case 'AddDataSourceRowsTask':
      return { name: 'Add Data Source Rows', icon: 'GeneralTask' };

    case 'UpdateDataSourceRowsTask':
      return { name: 'Update Data Source Rows', icon: 'GeneralTask' };

    case 'DeleteDataSourceRowsTask':
      return { name: 'Delete Data Source Rows', icon: 'GeneralTask' };

    case 'RefreshRefreshTokenTask':
      return { name: 'Refresh RefreshToken', icon: 'GeneralTask' };

    case 'CreateObjectTask':
      return { name: 'Salesforce Create Object', icon: 'Salesforce' };

    case 'GetLookupTask':
      return { name: 'Salesforce Get Object', icon: 'Salesforce' };

    case 'UpdateObjectTask':
      return { name: 'Salesforce Update Object', icon: 'Salesforce' };

    case 'UploadObjectAttachmentTask':
      return { name: 'Salesforce Upload Attachment', icon: 'Salesforce' };

    case 'UpsertObjectTask':
      return { name: 'Salesforce Upsert Object', icon: 'Salesforce' };

    case 'DelayTask':
      return { name: 'Test Delay', icon: 'GeneralTask' };

    case 'FailTask':
      return { name: 'Test Fail', icon: 'GeneralTask' };

    default:
      return { name: adapterName, icon: 'GeneralTask' };
  }
}

export function useAvailableActions(publishedApps: PublishedApp[]) {
  const [result, setResult] = useState<AvailableActions>({ actions: [], triggers: [] });

  useEffect(() => {
    const availableActions = getAvailableActionsFromMetadata(publishedApps);
    setResult(availableActions);
  }, [publishedApps]);

  return [result];
}

function getWebhooksMetadata(appId: string, webhookId: string): WebhookMetadata[] {
  const state = appContext.store.getState();
  const publishedApps = state.workfloweditor.publishedApps;
  const publishedApp = publishedApps ? publishedApps.find((x) => x.appId === appId) : null;
  const appMetadata = publishedApp ? publishedApp.package.metadata : null;
  if (!appMetadata || !appMetadata.webhooks) {
    return null;
  }
  return appMetadata.webhooks.filter((x) => !webhookId || x.id === webhookId);
}

function getEventsMetadata(appId: string): EventMetadata[] {
  const state = appContext.store.getState();
  const publishedApps = state.workfloweditor.publishedApps;
  const publishedApp = publishedApps ? publishedApps.find((x) => x.appId === appId) : null;
  const appMetadata = publishedApp ? publishedApp.package.metadata : null;
  if (!appMetadata || !appMetadata.events) {
    return null;
  }
  return appMetadata.events;
}

export function getAppMetadata(appId: string): AppMetadata {
  const state = appContext.store.getState();
  const publishedApps = state.workfloweditor.publishedApps;
  const publishedApp = publishedApps ? publishedApps.find((x) => x.appId === appId) : null;
  const appMetadata = publishedApp ? publishedApp.package.metadata : null;
  if (appMetadata) {
    return appMetadata;
  }
  return null;
}

function getStepMetadata(stepId: string, adapterName: string, appId: string, appCommand: string, workflowMetadata: WorkflowMetadata): StepMetadata {
  const state = appContext.store.getState();
  const publishedApps = state.workfloweditor.publishedApps;

  if (adapterName === 'apptask') {
    const publishedApp = publishedApps ? publishedApps.find((x) => x.appId === appId) : null;
    const appMetadata = publishedApp ? publishedApp.package.metadata : null;
    if (!appMetadata || !appMetadata.commands) {
      return null;
    }

    const commandMetadata = deepClone<CommandMetadata>(appMetadata.commands?.find((x) => x.name === appCommand));

    // dynamic metadata
    const workflowMetadataStep = workflowMetadata && workflowMetadata.steps ? workflowMetadata.steps.find((x) => x.id === stepId) : null;
    const usesDynamicMetadata = commandMetadata && commandMetadata.dynamicMetadata ? commandMetadata.dynamicMetadata : false;
    const dynamicMetadata = deepClone<DynamicMetadata>(
      workflowMetadataStep && workflowMetadataStep.dynamicMetadata ? workflowMetadataStep.dynamicMetadata : null
    );

    // inputs
    if (usesDynamicMetadata && dynamicMetadata && dynamicMetadata.inputs && commandMetadata && commandMetadata.inputs) {
      for (const dynamicInputMetadata of dynamicMetadata.inputs) {
        let stepInputMetadata = commandMetadata.inputs.find((x) => x.key === dynamicInputMetadata.key);
        if (!stepInputMetadata) {
          stepInputMetadata = {
            key: dynamicInputMetadata.key,
            label: dynamicInputMetadata.key,
            type: dynamicInputMetadata.type || 'text'
          };
          commandMetadata.inputs.push(stepInputMetadata);
        }
        if (dynamicInputMetadata.properties) {
          if (!stepInputMetadata.properties) {
            stepInputMetadata.properties = [];
          }
          for (const dynamicInputMetadataProperty of dynamicInputMetadata.properties) {
            let stepInputMetadataProperty = stepInputMetadata.properties.find((x) => x.key === dynamicInputMetadataProperty.key);
            if (!stepInputMetadataProperty) {
              stepInputMetadataProperty = {
                key: dynamicInputMetadataProperty.key,
                label: dynamicInputMetadataProperty.key,
                type: dynamicInputMetadataProperty.type || 'text'
              };
              stepInputMetadata.properties.push(stepInputMetadataProperty);
            }
            mergeDeep(stepInputMetadataProperty, dynamicInputMetadataProperty);
          }
          delete dynamicInputMetadata.properties;
        }
        mergeDeep(stepInputMetadata, dynamicInputMetadata);
      }
    }
    // outputs
    if (usesDynamicMetadata && dynamicMetadata && dynamicMetadata.outputs && commandMetadata && commandMetadata.outputs) {
      for (const dynamicOutputMetadata of dynamicMetadata.outputs) {
        let stepOutputMetadata = commandMetadata.outputs.find((x) => x.key === dynamicOutputMetadata.key);
        if (!stepOutputMetadata) {
          stepOutputMetadata = {
            key: dynamicOutputMetadata.key,
            type: dynamicOutputMetadata.type || 'text',
            label: dynamicOutputMetadata.label,
            helpText: dynamicOutputMetadata.helpText
          };
          commandMetadata.outputs.push(stepOutputMetadata);
        }
        mergeDeep(stepOutputMetadata, dynamicOutputMetadata);
      }
    }

    return commandMetadata;
  }

  const ihTask = IHMetadata.steps.find((x) => x.adapterName === adapterName);
  return ihTask;
}

const _loadingTemplates: Record<string, AxiosPromise<TypeMePlease>> = {};
const _templateFields: Record<string, TypeMePlease> = {};

async function getTemplateFields(templateId: string) {
  if (_templateFields[templateId]) {
    return _templateFields[templateId];
  }
  if (!_loadingTemplates[templateId]) {
    _loadingTemplates[templateId] = apimCall(GoFormzServices.TemplateService, `/templates/${templateId}?includeItemCollections=false`)
      .then((templateResult) => {
        _templateFields[templateId] = templateResult.data.template.fields;
      })
      .catch((err) => {
        //We can't differentiate between templates and template folders here so we'll just eat 404s
        //If a template gets deleted, it'll get removed from the workflow anyways
        if (err.response.status !== 404) {
          console.error(err);
        }

        _templateFields[templateId] = [];
      })
      .then(() => {
        delete _loadingTemplates[templateId];
        return _templateFields[templateId];
      });
  }
  return _loadingTemplates[templateId];
}

export function useStepsMetadata(workflow: Workflow, publishedApps: PublishedApp[], workflowMetadata: WorkflowMetadata) {
  const [result, setResult] = useState<StepsMetadata>([]);

  useEffect(() => {
    if (!workflow) {
      return;
    }

    getStepsMetadata(workflow, workflowMetadata).then((stepsMetadata) => {
      setResult(stepsMetadata);
    });
  }, [workflow, publishedApps, workflowMetadata]);

  return [result];
}

export async function getStepsMetadata(workflow: Workflow, workflowMetadata: WorkflowMetadata): Promise<StepsMetadata> {
  const result: StepsMetadata = [];

  if (workflow.trigger) {
    const triggerOutputs: StepOutputMetadata[] = [];
    const triggerType = workflow.trigger.type;
    const eventType = workflow.trigger.eventType;

    if (triggerType === 'GoFormzEvent') {
      if (eventType === 'FormComplete' || eventType === 'FormTransfer' || eventType === 'FormPublicShare' || eventType === 'FormCreate') {
        triggerOutputs.push({
          key: 'formId',
          type: 'text',
          label: 'Form ID',
          helpText: 'The unique 32-digit identifier assigned to each individual Form.',
          tags: ['formId']
        });

        let actionVerb: string;
        switch (eventType) {
          case 'FormComplete':
            actionVerb = 'completed';
            break;
          case 'FormTransfer':
            actionVerb = 'transferred';
            break;
          case 'FormPublicShare':
            actionVerb = 'submitted';
            break;
          case 'FormCreate':
            actionVerb = 'created';
            break;
        }
        triggerOutputs.push({ key: 'formName', type: 'text', label: 'Form Name', helpText: `Indicates the ${actionVerb} Form name.` });

        triggerOutputs.push({
          key: 'formTemplateId',
          type: 'text',
          label: 'Form Template ID',
          helpText: 'This ID references the Template used to create this Form.'
        });
        triggerOutputs.push({
          key: 'formStatus',
          type: 'text',
          label: 'Form Status',
          helpText: 'Indicates the status you want changed for your Form. A Form will be either "Drafted" or "Complete".'
        });
        triggerOutputs.push({
          key: 'formChangeDate',
          type: 'text',
          label: 'Date of Form Change',
          helpText: 'The time when the Form\'s status was last updated (including completions and transfers) formatted as "MM/DD/YY hh:mm:ss".'
        });
        triggerOutputs.push({
          key: 'formLastUpdateDate',
          type: 'text',
          label: 'Date of Form Update',
          helpText: 'The time when the Form was last updated formatted as "MM/DD/YY hh:mm:ss".'
        });
        triggerOutputs.push({
          key: 'formVersionId',
          type: 'text',
          label: 'Form Version ID',
          helpText: 'This ID references the Form version used to trigger this Workflow.'
        });
        triggerOutputs.push({
          key: 'formAssignmentId',
          type: 'text',
          label: 'Form Assignment ID',
          helpText: 'Indicates the ID for the User or Group to which this Form is currently assigned.'
        });
        triggerOutputs.push({
          key: 'templateName',
          type: 'text',
          label: 'Template Name',
          helpText: 'The name of the template used to create the form.'
        });
        triggerOutputs.push({
          key: 'formCreatedDate',
          type: 'text',
          label: 'Form Created Date',
          helpText: 'Date that the Form was created.'
        });
        triggerOutputs.push({
          key: 'templateVersionId',
          type: 'text',
          label: 'Template Version ID',
          helpText: 'This ID references the Template version used by the Form that triggers this Workflow.'
        });
        triggerOutputs.push({
          key: 'username',
          type: 'text',
          label: 'Username',
          helpText: 'Username for the User or Group to which this Form is currently assigned.'
        });
        triggerOutputs.push({
          key: 'workflowVersionId',
          type: 'text',
          label: 'Workflow Version ID',
          helpText: 'This ID references the Workflow version used in this Workflow.'
        });

        // get fields
        const templateId = !!workflow.trigger.entityIds && workflow.trigger.entityIds.length === 1 ? workflow.trigger.entityIds[0] : workflow.trigger.entityId;
        if (templateId) {
          const templateFields = await getTemplateFields(templateId);
          if (templateFields) {
            for (const field of templateFields
              .filter((field) => !(field.columns && field.type === 12)) //Omit Table fields
              .sort((a: any, b: any) => a.name.localeCompare(b.name.toLowerCase()))) {
              if (!triggerOutputs.some((x) => x.key === field.name)) {
                triggerOutputs.push({ key: field.name, type: 'text' });
              }
            }
          }
        }
      } else if (eventType === 'FormDelete') {
        triggerOutputs.push({
          key: 'formId',
          type: 'text',
          label: 'Form ID',
          helpText: 'The unique 32-digit identifier assigned to each individual Form.',
          tags: ['formId']
        });
        triggerOutputs.push({
          key: 'formVersionNumber',
          type: 'text',
          label: 'Form Version Number',
          helpText: 'This number references the Form version number used to trigger this Workflow.'
        });
        triggerOutputs.push({
          key: 'templateName',
          type: 'text',
          label: 'Template Name',
          helpText: 'The name of the template used to create the Form.'
        });
        triggerOutputs.push({
          key: 'workflowVersionId',
          type: 'text',
          label: 'Workflow Version ID',
          helpText: 'This ID references the Workflow version used in this Workflow.'
        });
      }
    } else {
      triggerOutputs.push({
        key: 'workflowVersionId',
        type: 'text',
        label: 'Workflow Version ID',
        helpText: 'This ID references the Workflow version used in this Workflow.'
      });
    }

    let webhooks: WebhookMetadata[] = null;
    let events: EventMetadata[] = null;

    if (triggerType === 'appevent') {
      webhooks = getWebhooksMetadata(workflow.trigger.appId, null);
      events = getEventsMetadata(workflow.trigger.appId);
    }
    if (triggerType === 'appwebhook') {
      webhooks = getWebhooksMetadata(workflow.trigger.appId, workflow.trigger.entityId);
      if (webhooks && webhooks.length === 1) {
        const webhook = webhooks[0];
        if (webhook.outputs) {
          for (const output of webhook.outputs) {
            triggerOutputs.push(output);
          }
        }
      }
    }

    result.push({ id: 'trigger', metadata: { outputs: triggerOutputs, webhooks, events } });
  }

  const steps = getWorkflowSteps(workflow);

  for (const step of steps) {
    const adapterName = step.action.adapterName;
    const stepMetadata = getStepMetadata(step.id, adapterName, step.config.appId, step.config.appCommand, workflowMetadata);

    result.push({ id: step.id, metadata: stepMetadata });
  }

  return result;
}

function getWorkflowSteps(workflow: Workflow) {
  let stepArray: WorkflowStep[] = [];
  if (workflow.steps) {
    stepArray = workflow.steps;
  }
  return stepArray;
}

export function getAvailableVariables(steps: StepsMetadata, stepId: string) {
  const variables: AvailableVariables = {
    steps: [],
    knownExpressions: []
  };

  for (const step of steps) {
    if (step.id === stepId) {
      break;
    }

    variables.steps.push({
      id: step.id
    });

    if (step.metadata && step.metadata.outputs) {
      for (const output of step.metadata.outputs) {
        const alternateExpressions = [];

        const expression = `#{${step.id}.${output.key}}`;
        alternateExpressions.push(`#{${step.id}.[${output.key}]}`);

        variables.knownExpressions.push({
          key: output.key,
          type: output.type,
          friendlyName: output.label,
          description: output.helpText,
          hidden: output.hidden,
          stepId: step.id,
          expression,
          alternateExpressions,
          tags: output.tags
        });
      }
    }
  }

  return variables;
}

export function metadataHasInputKey(metadata: StepMetadata, key: string) {
  if (!metadata || !metadata.inputs) {
    return false;
  }
  return metadata.inputs.some((x) => x.key === key || (x.alternateKeys && x.alternateKeys.some((ak) => ak === key)));
}

export function getUsedKeyInConfig(config: WorkflowStepConfig, input: StepInputMetadata, adapterName: string) {
  const key = adapterName === 'apptask' ? `appinput_${input.key}` : input.key;
  if (config[key] !== undefined) {
    return key;
  }
  if (input.alternateKeys) {
    for (const alternateKey of input.alternateKeys) {
      const aKey = adapterName === 'apptask' ? `appinput_${alternateKey}` : alternateKey;
      if (config[aKey] !== undefined) {
        return aKey;
      }
    }
  }
  return key;
}
